import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { categorySlice } from '../../reducers'
import { Close } from '@mui/icons-material'
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Link,
  Slider,
  TextField,
  Typography
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import Autocomplete from '@mui/material/Autocomplete'
import Dropdown from './dropdown'
import Stepper from './stepper'
import { useStyles } from './style'
import { routes } from '../../routes'
import { AVAILABLE_ROLES } from '../../helpers/user-preferences'
import { useTenant } from '@emerald-works-nova/auth'
import consts from '../../consts'

const MAX_SELECTION_PER_CATEGORY = 5

const Onboarding = ({ profile, hasNextPhase, onClose, onFinish }) => {
  const classes = useStyles()
  const [activeStep, setActiveStep] = useState(0)
  const [expanded, setExpanded] = useState(-1)
  const [learningExperience, setLearningExperience] = useState({
    favoriteTopics: profile.favoriteTopics || [],
    otherTopics: profile.otherTopics || [],
    role: profile.role,
    seniorityLevel: profile.seniorityLevel || 'entry'
  })
  const categories = useSelector(categorySlice.selectors.selectCategoryDefaultHierarchy)

  const tenant = useTenant()
  const isClub = tenant.pk === consts.DEFAULT_CLUB_PK
  const seniorityQuestion = isClub ? 'What is your seniority level?' : 'How would you assess your career experience?'

  const SENIORITY_LEVELS = [
    { value: 1, label: isClub ? 'Entry' : 'Developing', stringValue: 'entry' },
    { value: 2, label: isClub ? 'Mid' : 'Proficient', stringValue: 'mid' },
    { value: 3, label: isClub ? 'Senior' : 'Accomplished', stringValue: 'senior' }
  ]

  const allAvailableOptions = useMemo(() => {
    return (categories || [])
      .filter((parent) => (parent?.children) && !parent.hideFromLPQ)
      .map((parent, rootLevel) => parent.children
        .filter(child => !child.hideFromLPQ)
        .map(child => ({ sk: child.sk, name: child.name, rootLevel, parentSk: parent.sk, parentName: parent.name, hideFromLPQ: child.hideFromLPQ })))
      .flat()
  }, [categories])

  const availableCategories = useMemo(() => {
    return categories
      .filter((mainCategory) => (mainCategory?.children) && !mainCategory.hideFromLPQ)
      .map((mainCategory) => {
        return {
          ...mainCategory,
          children: mainCategory.children.filter(child => !child.hideFromLPQ)
        }
      })
  }, [categories])

  const handleChangeFavorites = (obj) => {
    if (obj.children.length < 6) {
      const temp = [...learningExperience.favoriteTopics]
      const finds = temp.find(x => x.sk === obj.sk)
      if (finds) {
        const index = temp?.indexOf(finds)
        if (index >= 0) {
          temp[index].children = obj.children
        }
      } else {
        temp.push(obj)
      }
      setLearningExperience(curr => ({ ...curr, favoriteTopics: temp }))
    }
  }
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleFinish = () => {
    const partialProfile = { ...learningExperience }
    const favoriteTopicsSks = partialProfile.favoriteTopics.reduce((acc, cur) => [...cur.children, ...acc], [])
    const favoriteTopicsWithNameAndSk = favoriteTopicsSks.map(sk => allAvailableOptions.find(opt => opt.sk === sk))

    partialProfile.favoriteTopics = favoriteTopicsWithNameAndSk
    onFinish(partialProfile)
  }

  const renderDropdown = () => {
    return availableCategories
      .map((mainCategory, i) => {
        const find = learningExperience.favoriteTopics.find(x => x.sk === mainCategory.sk)
        const index = learningExperience.favoriteTopics.indexOf(find)
        return (
          <Dropdown
            key={`${mainCategory.sk}-${i}`}
            title={mainCategory.name}
            selected={learningExperience.favoriteTopics[index]?.children || []}
            optionList={mainCategory.children.map(category => ({ sk: category.sk, name: category.name }))}
            onChangeSelected={(obj) => (handleChangeFavorites({ sk: mainCategory.sk, name: mainCategory.name, children: obj }))}
            expand={expanded === i}
            handleExpand={() => ((expanded === i) ? setExpanded(-1) : setExpanded(i))}
          />
        )
      })
  }

  return (
    <>
      <Box className={classes.header}>
        <Box className={classes.title} display='flex'>
          <Typography><b>Customize your learning experience</b></Typography>
          <IconButton onClick={() => onClose('close-button')} size='large'>
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <Box className={classes.subtitle}>
          <Typography>
            Tell us a bit about yourself so we can tailor your learning recommendations
          </Typography>
        </Box>
      </Box>
      <Box component='div' className={classes.overflow}>
        {availableCategories.length > 0 && (
          <Box display={activeStep === 0 ? 'flex' : 'none'} className={classes.content} component='div'>
            <Box>
              <Typography>
                <b>
                  What topics would you like to learn?
                </b>
              </Typography>
            </Box>
            <Divider />

            {renderDropdown()}

            <Box mt={2} mb={2} display='flex' flexDirection='column'>
              <Typography><b>Other</b></Typography>
              {
              learningExperience.otherTopics?.length > 0 &&
                <Typography variant='caption'>
                  {learningExperience.otherTopics.length} of {MAX_SELECTION_PER_CATEGORY} selected
                </Typography>
            }
              <Autocomplete
                multiple
                id='other-topics'
                options={[]}
                value={learningExperience.otherTopics || []}
                freeSolo
                onChange={(_, value) => (value.length < 6 && setLearningExperience(curr => ({ ...curr, otherTopics: value })))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={index} label={option} {...getTagProps({ index })} />
                  ))}
                renderInput={(params) => (
                  <TextField {...params} variant='standard' placeholder='Type to search' />
                )}
              />
            </Box>
          </Box>
        )}
        <Box display={activeStep === 1 ? 'flex' : 'none'} className={classes.content} component='div'>
          <Typography>
            <b>
              What is your department?
            </b>
          </Typography>

          <Autocomplete
            id='select-role-field'
            options={AVAILABLE_ROLES.map(role => role.value)}
            getOptionLabel={option => AVAILABLE_ROLES.find(role => role.value === option).title}
            style={{ width: 300 }}
            value={learningExperience.role}
            onChange={(_, value) => setLearningExperience(curr => ({ ...curr, role: value }))}
            renderInput={(params) => <TextField {...params} variant='standard' placeholder='Type to search' />}
          />
          <Typography>
            <b>
              {seniorityQuestion}
            </b>
          </Typography>
          <div style={{ padding: 20 }}>
            <Slider
              defaultValue={1}
              min={1}
              max={3}
              style={{ width: '100%' }}
              marks={SENIORITY_LEVELS}
              getAriaValueText={(value) => `${value} level`}
              onChangeCommitted={(_, value) => setLearningExperience(curr => ({ ...curr, seniorityLevel: SENIORITY_LEVELS.find(s => s.value === value).stringValue }))}
              valueLabelFormat={value => SENIORITY_LEVELS.find((mark) => mark.value === value).label}
              step={null}
              aria-label='seniority level input'
              valueLabelDisplay='off'
            />
          </div>
        </Box>
      </Box>
      <Box className={classes.footer} component='div' zIndex='tooltip'>
        <Typography>
          You can always change your answers later in your <Link to={routes.profile.path} component={RouterLink} underline='always'>Profile</Link> and we&apos;ll send you reminders to update them in the future
        </Typography>
        <Stepper
          className={classes.stepper}
          handleClose={() => onClose('do-it-later-button')}
          handleBack={handleBack}
          handleNext={handleNext}
          handleFinish={handleFinish}
          hasNextPhase={hasNextPhase}
          activeStep={activeStep}
        />
      </Box>
    </>
  )
}

export default Onboarding
